import { ChangeEvent, useState } from 'react'
import {
  Filter,
  SelectArrayInput,
  SelectArrayInputProps,
  SelectInput,
  TextInput,
  TopToolbar,
} from 'react-admin'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { CheckboxDropdown } from 'shared/components/Dropdown/CheckboxDropdown'
import { Dropdown } from 'shared/components/Dropdown/Dropdown'
import { TextInput as SharedTextInput } from 'shared/components/TextInput/TextInput'
import { HandlerOf } from 'shared/helpers/typeHelper'
import { DOCUMENT_TYPES, SAFETY_TOOL_DOCUMENT_STATUS_CHOICES } from 'shared/models/document'
import styled from 'styled-components'

import { useHasPerformanceScores } from '../../hooks/useHasFeatureFlag'

interface SafetyToolListFilterValues {
  search: string
  document_type: string[]
  documents_statuses: string
}

interface SafetyToolListFilterProps {
  filterValues: Partial<SafetyToolListFilterValues>
  displayedFilters: Partial<Record<keyof SafetyToolListFilterValues, boolean>>
  setFilters: (values: Partial<SafetyToolListFilterValues>, filter: string, debounce?: number) => void
  hideFilter: HandlerOf<string>
  setTypes: HandlerOf<string[]>
  setDocumentStatuses: HandlerOf<string>
}

export const SafetyToolListFilter = (props: SafetyToolListFilterProps) => {
  const { filterValues, setFilters, setTypes, setDocumentStatuses } = props

  const hasPerformanceScoresFlag = useHasPerformanceScores()

  if (hasPerformanceScoresFlag) {
    return (
      <SafetyToolListFilterV2
        search={filterValues.search || ''}
        setSearch={(value) => setFilters({ ...filterValues, search: value }, 'search')}
        documentTypes={filterValues.document_type || []}
        setDocumentTypes={(value) => {
          setFilters({ ...filterValues, document_type: value }, 'search')
          setTypes(value)
        }}
        documentStatuses={filterValues.documents_statuses}
        setDocumentStatuses={(value) => {
          setFilters({ ...filterValues, documents_statuses: value }, 'search')
          setDocumentStatuses(value)
        }}
      />
    )
  }

  return (
    <Filter {...props}>
      <TextInput label="Search by Driver / Fleet" source="search" alwaysOn />
      <SelectArrayInput
        alwaysOn
        label="Document Type"
        source="document_type"
        choices={DOCUMENT_TYPES}
        onChange={(e: ChangeEvent<SelectArrayInputProps>) => setTypes(e.target.value as string[])}
        style={{ minWidth: '180px' }}
      />
      <SelectInput
        alwaysOn
        label="Document Status"
        source="documents_statuses"
        choices={SAFETY_TOOL_DOCUMENT_STATUS_CHOICES}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setDocumentStatuses(e.target.value as string)}
        style={{ minWidth: '170px' }}
      />
    </Filter>
  )
}

function SafetyToolListFilterV2({
  search,
  setSearch,
  documentTypes,
  setDocumentTypes,
  documentStatuses,
  setDocumentStatuses,
}: {
  search: string
  setSearch: HandlerOf<string>
  documentTypes: string[]
  setDocumentTypes: HandlerOf<string[]>
  documentStatuses: string | undefined
  setDocumentStatuses: HandlerOf<string>
}) {
  const [searchValue, setSearchValue] = useState(search)

  return (
    <FlexBox gap="8px" isFullWidth>
      <FlexBox width="240px">
        <SharedTextInput
          label="Search by Driver / Fleet"
          value={searchValue}
          onChange={(e: any) => {
            setSearchValue(e.target.value)
            setSearch(e.target.value)
          }}
        />
      </FlexBox>
      <FlexBox width="240px">
        <CheckboxDropdown
          label="Document Type"
          options={DOCUMENT_TYPES.map((type) => ({
            key: type.id,
            value: documentTypes?.includes(type.id) || false,
            label: type.name,
            onChange: (value: boolean) => {
              setDocumentTypes([
                ...(documentTypes || []).filter((t) => t !== type.id),
                ...(value ? [type.id] : []),
              ])
            },
          }))}
          width={340}
          maxHeight={400}
          noSearchOption
          onSelectAll={(selectedAll) => {
            setDocumentTypes(selectedAll ? DOCUMENT_TYPES.map((type) => type.id) : [])
          }}
          isFullWidth
        />
      </FlexBox>
      <FlexBox width="240px">
        <Dropdown
          label="Document Status"
          value={documentStatuses}
          options={[
            { key: 'all', value: 'all', label: 'All' },
            ...SAFETY_TOOL_DOCUMENT_STATUS_CHOICES.map((status) => ({
              key: status.id,
              value: status.id,
              label: status.name,
            })),
          ]}
          onChange={(value: string) => setDocumentStatuses(value)}
          width={210}
          isFullWidth
        />
      </FlexBox>
    </FlexBox>
  )
}

export const StyledTopToolbar = styled(TopToolbar)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  margin-bottom: 24px;
`
